import { MeResponse } from '../../core/auth/services/auth.service';
import { TablePaginatedFilters } from '../../core/components/Generics/Table/SwTable/Table';

/**
 * Qué columnas y filtros del listado de siniestros ve el usuario de la sesión, según su `/me/`. Solo visibilidad: el
 * backend decide qué siniestros devuelve. No usar con el `/me/` del token de la URL (no trae `is_smartway` /
 * `is_auditor`, y el usuario contaría como tasador).
 *
 * | Rol | Columna y filtro Aseguradora | Filtro Tasadores |
 * |---|---|---|
 * | Smartway | Sí | Sí |
 * | Auditor (no Smartway) | No | Sí |
 * | Tasador (ninguno) | Sí | No |
 */
type SessionUser = Pick<MeResponse, 'customers' | 'is_smartway' | 'is_auditor'> | null | undefined;

export interface CustomerOption {
    id: number;
    name: string;
}

/** Columnas del listado que no se pueden ordenar en backend: se sacan del `sort_by` antes del request. */
export const UNSORTABLE_CLAIM_COLUMNS: ReadonlyArray<string> = ['customer_name', 'customer'];

const customersOf = (user: SessionUser): Record<string, string> => (user && user.customers) || {};

const hasCustomers = (user: SessionUser): boolean => Object.keys(customersOf(user)).length > 0;

export const isSmartwayUser = (user: SessionUser): boolean => !!(user && user.is_smartway);

export const isAuditorUser = (user: SessionUser): boolean => !!(user && user.is_auditor);

/** Opciones del filtro Aseguradora (`{ id, name }`), ordenadas por nombre. */
export const customerOptions = (user: SessionUser): CustomerOption[] =>
    Object.entries(customersOf(user))
        .map(([id, name]) => ({ id: Number(id), name: String(name) }))
        .filter(option => Number.isFinite(option.id))
        .sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));

/** Nombre de la aseguradora `id`; sin nombre en el mapa, el id como texto; sin id, `''`. */
export const customerName = (customers: Record<string, string> | undefined, id: number | null | undefined): string => {
    if (id === undefined || id === null) return '';
    return (customers && customers[String(id)]) || String(id);
};

/** Columna Aseguradora: hay aseguradoras y el usuario es Smartway o no es auditor. */
export const showCustomerColumn = (user: SessionUser): boolean =>
    hasCustomers(user) && (isSmartwayUser(user) || !isAuditorUser(user));

/** Filtro Aseguradora: hay aseguradoras y el usuario no es auditor. */
export const showCustomerFilter = (user: SessionUser): boolean => hasCustomers(user) && !isAuditorUser(user);

/** Filtro Tasadores: solo auditores y Smartway. */
export const showAppraiserFilter = (user: SessionUser): boolean => isAuditorUser(user) || isSmartwayUser(user);

/**
 * Copia de los parámetros sin las columnas no ordenables en `sort_by` (protege contra un snapshot guardado en
 * `localStorage` de cuando se podía ordenar). Si no queda ninguna, también se quita `sort_type`.
 */
export const stripUnsortableSort = <T extends TablePaginatedFilters>(params: T): T => {
    const next: T = { ...params };
    if (next.sort_by && next.sort_by.length > 0) {
        const sortable = next.sort_by.filter(field => !UNSORTABLE_CLAIM_COLUMNS.includes(field));
        if (sortable.length > 0) {
            next.sort_by = sortable;
        } else {
            delete next.sort_by;
            delete next.sort_type;
        }
    }
    return next;
};
